import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { runningCompleteProfilePath, accountPath } from '../utils/paths';
import {
  RUNNER_EXPERIENCE_LEVELS,
  RUNNER_WEEKLY_MILEAGE,
  RUNNER_PREFERRED_DISTANCES,
  RUNNER_GROUP_PREFERENCES,
  formatRunnerExperience,
  formatRunnerWeeklyMileage,
  formatRunnerGroupPreference,
  formatRunnerDistance,
  formatPace,
} from '../config/runnerProfileOptions';
import './PlayerRegistration.css';
import './RunningProfile.css';
import './AccountProfile.css';

const emptySportForm = {
  experienceLevel: 'BEGINNER',
  typicalPaceMinPerMile: '',
  weeklyMileage: '',
  groupPreference: '',
  preferredDistances: [],
  injuryNotes: '',
};

const RunningProfile = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(emptySportForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [runnerData, accountData] = await Promise.all([
          api.getMyRunnerProfile(user.id),
          api.getAccountProfile(user.id).catch(() => null),
        ]);
        setProfile(runnerData);
        setAccount(accountData);
        setFormData({
          experienceLevel: runnerData.experienceLevel || 'BEGINNER',
          typicalPaceMinPerMile: runnerData.typicalPaceMinPerMile ?? '',
          weeklyMileage: runnerData.weeklyMileage || '',
          groupPreference: runnerData.groupPreference || '',
          preferredDistances: runnerData.preferredDistances || [],
          injuryNotes: runnerData.injuryNotes || '',
        });
      } catch {
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
    setSuccess('');
  };

  const toggleDistance = (distance) => {
    setFormData((prev) => {
      const selected = prev.preferredDistances.includes(distance)
        ? prev.preferredDistances.filter((d) => d !== distance)
        : [...prev.preferredDistances, distance];
      return { ...prev, preferredDistances: selected };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const updated = await api.saveRunnerProfile(user.id, {
        experienceLevel: formData.experienceLevel,
        typicalPaceMinPerMile: formData.typicalPaceMinPerMile
          ? parseFloat(formData.typicalPaceMinPerMile, 10)
          : null,
        weeklyMileage: formData.weeklyMileage || null,
        groupPreference: formData.groupPreference || null,
        preferredDistances: formData.preferredDistances,
        injuryNotes: formData.injuryNotes || null,
      });
      setProfile(updated);
      setEditing(false);
      setSuccess('Running profile updated.');
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="card running-profile-page">
        <p><Link to="/login">Log in</Link> to view your running profile.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading profile...</div>;
  }

  if (!profile) {
    return (
      <div className="card running-profile-page">
        <h2>Running profile</h2>
        <p>You have not set up a running profile yet.</p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate(runningCompleteProfilePath())}
        >
          Complete running profile
        </button>
      </div>
    );
  }

  const displayName = account?.name || profile.name;
  const displayPhone = account?.phoneNumber || profile.phoneNumber;
  const displayPhoto = account?.profilePictureUrl || profile.profilePictureUrl;

  return (
    <div className="card running-profile-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h2>My running profile</h2>
          <p className="form-help">Running pace, mileage, and group preferences.</p>
        </div>
        {!editing && (
          <button type="button" className="btn btn-secondary" onClick={() => setEditing(true)}>
            Edit running details
          </button>
        )}
      </div>

      <p className="account-profile-page__shared-note">
        Name, photo, and phone are managed on your{' '}
        <Link to={accountPath()}>account profile</Link>.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {!editing ? (
        <>
          {displayPhoto && (
            <div className="profile-picture-preview" style={{ marginBottom: '1rem' }}>
              <img src={displayPhoto.startsWith('http') ? displayPhoto : `${process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:8080'}${displayPhoto}`} alt="" />
            </div>
          )}
          <dl className="running-profile-page__detail-grid">
            <div>
              <dt>Name</dt>
              <dd>{displayName}</dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>{displayPhone || '—'}</dd>
            </div>
            <div>
              <dt>Experience</dt>
              <dd>{formatRunnerExperience(profile.experienceLevel)}</dd>
            </div>
            <div>
              <dt>Typical pace</dt>
              <dd>{formatPace(profile.typicalPaceMinPerMile)}</dd>
            </div>
            <div>
              <dt>Weekly mileage</dt>
              <dd>{formatRunnerWeeklyMileage(profile.weeklyMileage)}</dd>
            </div>
            <div>
              <dt>Group preference</dt>
              <dd>{formatRunnerGroupPreference(profile.groupPreference)}</dd>
            </div>
          </dl>
          {profile.preferredDistances?.length > 0 && (
            <>
              <h3>Preferred distances</h3>
              <ul className="running-profile-page__distance-list">
                {profile.preferredDistances.map((d) => (
                  <li key={d}>{formatRunnerDistance(d)}</li>
                ))}
              </ul>
            </>
          )}
          {profile.injuryNotes && (
            <p style={{ marginTop: '1rem', color: '#5a6b62' }}>
              <strong>Private notes:</strong> {profile.injuryNotes}
            </p>
          )}
          <p style={{ marginTop: '1.5rem' }}>
            Also play soccer? <Link to="/soccer/players/profile">Edit your soccer player profile →</Link>
          </p>
        </>
      ) : (
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label htmlFor="experienceLevel">Running experience *</label>
            <select id="experienceLevel" name="experienceLevel" value={formData.experienceLevel} onChange={handleChange} required>
              {RUNNER_EXPERIENCE_LEVELS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="typicalPaceMinPerMile">Typical easy pace (min/mile)</label>
            <input type="number" id="typicalPaceMinPerMile" name="typicalPaceMinPerMile" min="4" max="20" step="0.1" value={formData.typicalPaceMinPerMile} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="weeklyMileage">Weekly mileage</label>
            <select id="weeklyMileage" name="weeklyMileage" value={formData.weeklyMileage} onChange={handleChange}>
              <option value="">— Select —</option>
              {RUNNER_WEEKLY_MILEAGE.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Preferred distances</label>
            <div className="running-profile-page__chips">
              {RUNNER_PREFERRED_DISTANCES.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`running-profile-page__chip${formData.preferredDistances.includes(opt.value) ? ' is-selected' : ''}`}
                  onClick={() => toggleDistance(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="groupPreference">Group run preference</label>
            <select id="groupPreference" name="groupPreference" value={formData.groupPreference} onChange={handleChange}>
              <option value="">— Select —</option>
              {RUNNER_GROUP_PREFERENCES.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="injuryNotes">Injury notes (private)</label>
            <textarea id="injuryNotes" name="injuryNotes" value={formData.injuryNotes} onChange={handleChange} rows={3} maxLength={500} />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default RunningProfile;
